/**
 * Euler entity labels — the authoritative curator-NAME source for Euler-Earn.
 *
 * Euler's data API exposes only curator/owner ADDRESSES on a vault, but Euler
 * maintains a public entity registry (euler-xyz/euler-labels) mapping each
 * curator entity to its per-chain address book — one JSON file per chain, so
 * ONE request resolves every curator on that chain. (The same data is behind
 * `/v3/entities`, but that shape needs one request per entity for addresses.)
 *
 * The join replaces guessing the curator from the vault's name: the heuristic
 * stays only as the fallback for vaults whose governance addresses are not in
 * the registry.
 *
 * Failure-tolerant by design: raw.githubusercontent.com rate-limits host-wide
 * per egress IP (see GITHUB_RATE_LIMIT.md), so a fetch failure serves the
 * last-good map for the chain (however old) and an empty map only when there
 * has never been one — the caller then degrades to the name heuristic, never
 * to an error.
 */

const LABELS_BASE =
  'https://raw.githubusercontent.com/euler-xyz/euler-labels/master'

/** Re-fetch cadence; last-good is still served when a refresh fails. */
const TTL_MS = 30 * 60 * 1000

interface CacheHit {
  at: number
  map: Map<string, string>
}

const cache = new Map<string, CacheHit>()
const inflight = new Map<string, Promise<Map<string, string>>>()

/** Test seam. */
export function __resetEulerEntityCache(): void {
  cache.clear()
  inflight.clear()
}

async function fetchChainEntities(chainId: string): Promise<Map<string, string>> {
  const res = await fetch(`${LABELS_BASE}/${chainId}/entities.json`)
  if (!res.ok) throw new Error(`euler-labels ${chainId}: HTTP ${res.status}`)
  const json: any = await res.json()

  // entity → { name, addresses: { "0x…": "label", … } }. Collect every
  // address under its entity's display name; an address claimed by more than
  // one entity is dropped as ambiguous rather than guessed.
  const byAddress = new Map<string, Set<string>>()
  for (const entity of Object.values(json ?? {})) {
    const name = String((entity as any)?.name ?? '').trim()
    if (!name) continue
    for (const addr of Object.keys((entity as any)?.addresses ?? {})) {
      const lower = addr.toLowerCase()
      if (!/^0x[0-9a-f]{40}$/.test(lower)) continue
      if (!byAddress.has(lower)) byAddress.set(lower, new Set())
      byAddress.get(lower)!.add(name)
    }
  }
  const map = new Map<string, string>()
  for (const [addr, names] of byAddress) {
    if (names.size === 1) map.set(addr, [...names][0])
  }
  return map
}

/**
 * Address (lowercased) → curator entity name for a chain. Never throws;
 * resolves to the freshest map obtainable (possibly empty).
 */
export async function fetchEulerEntityNames(
  chainId: string,
): Promise<Map<string, string>> {
  const hit = cache.get(chainId)
  if (hit && Date.now() - hit.at <= TTL_MS) return hit.map

  const running = inflight.get(chainId)
  if (running) return running

  const p = fetchChainEntities(chainId)
    .then((map) => {
      cache.set(chainId, { at: Date.now(), map })
      return map
    })
    .catch(() => {
      // Serve last-good past its TTL rather than dropping every curator name
      // because GitHub rate-limited one refresh.
      return hit?.map ?? new Map<string, string>()
    })
    .finally(() => {
      inflight.delete(chainId)
    })
  inflight.set(chainId, p)
  return p
}

/**
 * Resolve a vault's curator name from the entity map: the curator address
 * first (it names the actual operator), then the owner. `undefined` when
 * neither is registered — callers fall back to the name heuristic.
 */
export function eulerEntityCuratorName(
  entityNames: Map<string, string>,
  curator: string | undefined,
  owner: string | undefined,
): string | undefined {
  return (
    (curator ? entityNames.get(curator.toLowerCase()) : undefined) ??
    (owner ? entityNames.get(owner.toLowerCase()) : undefined)
  )
}
